import _ from 'lodash';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';

import {DraftMessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {isTeamNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {wrapActionCreator} from '../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {
  AppParametricSelector,
  createSelector,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {mergeAsyncStatuses} from '../../../../helpers/resourceTableStatusHelpers';
import {AnswerFolderModel} from '../../../../models/api/answerFolderModel';
import {AnswerModel} from '../../../../models/api/answerModel';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {AppState} from '../../../../store/appState';
import {
  getAnswerFoldersAsyncStatus,
  getSortedAnswerFolders,
} from '../../../../store/data/answerFolders/answerFoldersSelectors';
import {fetchAnswersAndFoldersAction} from '../../../../store/data/answers/answerActions';
import {isAnswerAvailable} from '../../../../store/data/answers/answersAvailabilityHelpers';
import {
  getAnswers,
  getAnswersAsyncStatus,
  getSortedAnswers,
} from '../../../../store/data/answers/answersSelectors';
import {getChannels} from '../../../../store/data/channels/channelsSelectors';
import {getUserParticipatingInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {addRecentAnswerAction} from '../../../../store/data/preferences/preferencesActions';
import {getRecentAnswerIds} from '../../../../store/data/preferences/preferencesSelectors';
import {getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {getTeamsByNamespace, getUserTeams} from '../../../../store/data/teams/teamsSelectors';
import {AnswersDropdown} from './answersDropdown';

/*
 * Props.
 */

interface AnswersDropdownContainerProps {
  channelId: number;
  message?: DraftMessageModel;
  conversation: ConversationModel | null;
  isQuickInsert?: boolean;
}

/*
 * Selectors.
 */

function getChannelId(_state: AppState, props: AnswersDropdownContainerProps) {
  return props.channelId;
}

function getMessage(_state: AppState, props: AnswersDropdownContainerProps) {
  return props.message;
}

function getConversation(_state: AppState, props: AnswersDropdownContainerProps) {
  return props.conversation;
}

function getIsQuickInsert(_state: AppState, props: AnswersDropdownContainerProps) {
  return Boolean(props.isQuickInsert);
}

const getMessageUid = createSelector([getMessage], (message) => message?.uid);

function makeGetChannel() {
  return createSelector([getChannels, getChannelId], (channels, channelId) => {
    const channel = channels[channelId];
    if (!channel) {
      throw new Error('A channel with the specified ID could not be found.');
    }

    return channel;
  });
}

function makeGetNamespace() {
  return createSelector([makeGetChannel()], (channel) => channel.namespace);
}

function makeGetAvailableUserNamespaces() {
  return createSelector([makeGetNamespace(), getUser, getUserTeams], (namespace, user, userTeams) => {
    // If this is a shared namespace, just add ours.
    if (isTeamNamespace(namespace)) {
      return [user.namespace, namespace];
    }

    // Otherwise, return all the namespaces our teammate has access to.
    return [user.namespace, ...userTeams.map((t) => t.namespace)];
  });
}

const getAsyncStatus = createSelector(
  [getAnswersAsyncStatus, getAnswerFoldersAsyncStatus],
  (answersAsyncStatus, answerFoldersAsyncStatus) =>
    mergeAsyncStatuses([answersAsyncStatus, answerFoldersAsyncStatus]),
);

type GetAnswerListSelector = AppParametricSelector<any, ReadonlyArray<AnswerModel>>;
type GetAnswerFolderListSelector = AppParametricSelector<any, ReadonlyArray<AnswerFolderModel>>;

function makeGetAvailableAnswers(getAnswerList: GetAnswerListSelector) {
  return createSelector(
    [
      getAnswerList,
      makeGetAvailableUserNamespaces(),
      makeGetChannel(),
      getMessage,
      getConversation,
      getUserParticipatingInboxes,
      makeGetNamespace(),
    ],
    (answers, namespaces, channel, message, conversation, participatingInboxes, channelNamespace) =>
      answers.filter((a) =>
        isAnswerAvailable(
          a,
          namespaces,
          channel,
          message,
          conversation,
          participatingInboxes,
          channelNamespace,
        ),
      ),
  );
}

function makeGetAvailableFolders(getAnswerFolderList: GetAnswerFolderListSelector) {
  return createSelector([getAnswerFolderList, makeGetAvailableUserNamespaces()], (folders, namespaces) =>
    // Filter out folders in namespaces this user does not have access to.
    folders.filter((folder) => namespaces.includes(folder.namespace)),
  );
}

function makeGetRecentAnswers() {
  return createSelector([getAnswers, getRecentAnswerIds], (answers, recentAnswerIds) =>
    _(recentAnswerIds)
      .map((answerId) => answers[answerId])
      .compact()
      .value(),
  );
}

/*
 * Redux.
 */

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    addRecentAnswerId: wrapActionCreator(dispatch, addRecentAnswerAction),
    fetchAllAnswersAndFolders: wrapActionCreator(dispatch, fetchAnswersAndFoldersAction),
  };
}

const mapStateToProps = makeMapStateToProps<AnswersDropdownContainerProps>()(() => ({
  allAnswers: makeGetAvailableAnswers(getSortedAnswers),
  allFolders: makeGetAvailableFolders(getSortedAnswerFolders),
  recentAnswers: makeGetAvailableAnswers(makeGetRecentAnswers()),
  teams: getTeamsByNamespace,
  loadingStatus: getAsyncStatus,
  messageUid: getMessageUid,
  isQuickInsert: getIsQuickInsert,
}));

export const AnswersDropdownContainer = connect(mapStateToProps, mapDispatchToProps)(AnswersDropdown);
